<script setup lang="ts">
import type { ComputedRef } from 'vue'
import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    provide,
    ref,
    shallowRef,
    useTemplateRef,
    watch,
    watchEffect,
} from 'vue'
import type { EmblaOptionsType } from 'embla-carousel'
import emblaCarouselVue from 'embla-carousel-vue'
import { useMutationObserver } from '@vueuse/core'
import {
    CarouselDesktopSizes,
    ECarouselAlign,
    ECarouselDesktopWidth,
    EMobileSlidesVariants,
    type ICarousel,
} from './types'
import useCarouselConfig from './composables/useCarouselConfig'
import { SIZES } from '@/ui/ts/types/Sizes'
import { isDark } from '@/lib/utils'
import { EBreakpoints } from '@/ui/composables/useBreakpoint'

const props = withDefaults(defineProps<ICarousel>(), {
    desktopSize: CarouselDesktopSizes.LARGE,
    desktopWidth: ECarouselDesktopWidth.GRID,
    desktopSnap: false,
    mobileSlidesVariant: EMobileSlidesVariants.FULLWIDTH,
    tabletSlidesVariant: EMobileSlidesVariants.FULLWIDTH,
    inContainer: true,
    align: ECarouselAlign.START,
})

const carouselConfig = shallowRef({} as Record<string, string>)

const canScrollPrev = ref(false)
const canScrollNext = ref(false)

watchEffect(() => {
    carouselConfig.value = useCarouselConfig(props, canScrollPrev, canScrollNext)
})

// EMBLA CONFIGURATION
const dragFree = ref(true)
const isActive = ref(true)
const mobileMediaQuery = ref<MediaQueryList | null>(null)

const computedEmblaOptions: ComputedRef<EmblaOptionsType> = computed(() => ({
    active: isActive.value,
    dragFree: dragFree.value,
    align: props.align,
    loop: props.loop,
    breakpoints: {
        [`(max-width: ${EBreakpoints.MD}px)`]: {
            active: !props.disableMobile && isActive.value,
        },
        [`(min-width: ${EBreakpoints.MD}px)`]: {
            active: !props.disableTablet && isActive.value,
        },
        [`(min-width: ${EBreakpoints.LG}px)`]: {
            active: !props.disableDesktop && isActive.value,
        },
    },
}))
const [emblaRef, emblaApi] = emblaCarouselVue(computedEmblaOptions.value)

// provide emblaApi and props to slots
provide('emblaApi', emblaApi)
provide('carouselProps', props)

// emblaApi is exposed to the outside and accessible through references, e.g., emblaRef.value?.emblaApi.value.scrollNext()
defineExpose({
    emblaApi,
})
const buttonSize = ref(props.desktopSize === CarouselDesktopSizes.LARGE ? SIZES.MEDIUM : SIZES.SMALL)

async function updateButtons(): Promise<void> {
    if (!emblaApi.value) {
        return
    }

    await nextTick()

    canScrollPrev.value = emblaApi.value.canScrollPrev()
    canScrollNext.value = emblaApi.value.canScrollNext()
}

function scrollPrev(): void {
    if (emblaApi.value) {
        emblaApi.value.scrollPrev()
    }
}

function scrollNext(): void {
    if (emblaApi.value) {
        emblaApi.value.scrollNext()
    }
}

function initSlidesAttrs(): void {
    if (!emblaApi.value) {
        return
    }

    const slides = emblaApi.value.slideNodes()

    slides.forEach((slide, index) => {
        slide.setAttribute('id', `carousel-item-${index + 1}`)
    })
}

function updateVisibleSlides(): void {
    if (!emblaApi.value) {
        return
    }

    const hiddenClass = /* tw */ 'lg:opacity-50'
    const slides = emblaApi.value.slideNodes()

    slides.forEach(slide => {
        slide.setAttribute('aria-current', 'false')

        if (props.desktopWidth === ECarouselDesktopWidth.FULL) {
            slide.classList.add(hiddenClass)
        }
    })

    emblaApi.value.slidesInView().forEach(index => {
        const visibleSlide = slides[index]

        visibleSlide.setAttribute('aria-current', 'true')

        if (props.desktopWidth === ECarouselDesktopWidth.FULL) {
            visibleSlide.classList.remove(hiddenClass)
        }
    })
}

function checkSlideWidth(): void {
    if (!emblaApi.value) {
        return
    }

    const slides = emblaApi.value.slideNodes()
    const container = emblaApi.value.containerNode()
    const containerWidth = container.getBoundingClientRect().width

    // Check if each slide has the same width as the container
    const allSlidesMatchWidth = slides.every(slide => {
        return slide.getBoundingClientRect().width === containerWidth
    })

    if (mobileMediaQuery.value?.matches) {
        // On mobile, disable dragFree if all slides have the same width as the container
        dragFree.value = !allSlidesMatchWidth
    } else {
        // On larger screens, set dragFree according to desktopSnap
        dragFree.value = !props.desktopSnap
    }

    emblaApi.value.reInit({ ...computedEmblaOptions.value })
}

function handleMediaQueryChange(): void {
    checkSlideWidth()
    updateCarouselActiveState()
    updateButtons()
}

// WCAG SLIDES COUNTER
const visibleSlidesStart = ref(1)
const visibleSlidesEnd = ref(1)
const totalSlides = ref(0)
const currentSlide = ref(0)

async function updateSlideCounter(): Promise<void> {
    if (!emblaApi.value) {
        return
    }

    await nextTick()

    totalSlides.value = emblaApi.value.slideNodes().length
    currentSlide.value = emblaApi.value.selectedScrollSnap()

    const slidesInView = emblaApi.value.slidesInView()

    if (slidesInView.length > 0) {
        visibleSlidesStart.value = slidesInView[0] + 1
        visibleSlidesEnd.value = slidesInView[slidesInView.length - 1] + 1
    }
}

// PROVIDE REACTIVE SLIDE COUNT AND CURRENT SLIDE INDEX TO DESCENDANT COMPONENTS
provide('totalSlides', totalSlides)
provide('currentSlide', currentSlide)

// KEYBOARD INTERACTION
function handleKeydown(event: KeyboardEvent, direction: 'prev' | 'next'): void {
    if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        if (direction === 'prev') {
            scrollPrev()
        } else {
            scrollNext()
        }
    }
}

// Updates the carousel's active state based on whether the total width of all slides exceeds the container's width.
function updateCarouselActiveState(): void {
    if (!emblaApi.value) {
        return
    }

    const slides = emblaApi.value.slideNodes()
    const container = emblaApi.value.containerNode()
    const containerWidth = container.getBoundingClientRect().width

    const totalSlidesWidth = slides.reduce((total, slide) => {
        return total + slide.offsetWidth
    }, 0)

    const allSlidesFit = totalSlidesWidth <= containerWidth

    isActive.value = !allSlidesFit

    emblaApi.value.reInit({ ...computedEmblaOptions.value })
}

// EMBLA EVENTS
function initCarousel(): void {
    if (!emblaApi.value) {
        return
    }

    initSlidesAttrs()
    updateButtons()
    updateVisibleSlides()
    updateSlideCounter()
    checkSlideWidth()
    updateCarouselActiveState()
}

onMounted(() => {
    if (emblaApi.value) {
        emblaApi.value.on('init', initCarousel)
        emblaApi.value.on('slidesChanged', initCarousel)
        emblaApi.value.on('resize', initCarousel)
        emblaApi.value.on('select', () => {
            updateButtons()
            updateSlideCounter()
            updateVisibleSlides()
        })
        emblaApi.value.on('slidesInView', () => {
            updateVisibleSlides()
            updateSlideCounter()
        })
        emblaApi.value.on('settle', updateSlideCounter)
    }

    mobileMediaQuery.value = window.matchMedia('(max-width: 1024px)')

    mobileMediaQuery.value.addEventListener('change', handleMediaQueryChange)
})

watch(
    () => computedEmblaOptions.value,
    () => {
        initCarousel()
    },
    { deep: true },
)

onBeforeUnmount(() => {
    if (emblaApi.value) {
        emblaApi.value.destroy()
    }

    mobileMediaQuery.value?.removeEventListener('change', handleMediaQueryChange)
})

// It monitors changes in the child list and triggers initCarousel() when mutations occur.
const slidesContainerRef = useTemplateRef('slidesContainerRef')

useMutationObserver(
    slidesContainerRef,
    mutations => {
        const relevantMutations = mutations.some(m => m.type === 'childList')
        if (relevantMutations) {
            initCarousel()
        }
    },
    {
        childList: true,
        subtree: true,
    },
)
</script>

<template>
    <div
        :class="[isDark(dark), carouselConfig.rootClasses]"
        data-gpp-tools-ui
    >
        <div :class="carouselConfig.containerClasses">
            <div class="relative">
                <div
                    ref="emblaRef"
                    :class="carouselConfig.carouselClasses"
                    aria-roledescription="Karuzela"
                    :aria-label="carouselAriaLabel"
                    aria-live="polite"
                    role="region"
                    aria-atomic="true"
                >
                    <div
                        id="carousel-slides"
                        ref="slidesContainerRef"
                        :class="[carouselConfig.slidesContainerConfig, customSlidesContainerClasses]"
                    >
                        <slot v-bind="{ props }" />
                    </div>
                    <div
                        class="sr-only"
                        aria-live="polite"
                        aria-atomic="true"
                    >
                        Slajdy {{ visibleSlidesStart }}–{{ visibleSlidesEnd }} z {{ totalSlides }}
                    </div>
                </div>
                <div class="absolute top-1/2 w-full -translate-y-1/2">
                    <div class="container relative">
                        <VIconButton
                            v-if="totalSlides > 1"
                            variant="carousel"
                            :size="buttonSize"
                            icon="chevron-right"
                            data-test="carousel button right"
                            aria-controls="carousel-slides"
                            aria-label="Następny slajd"
                            :class="[carouselConfig.buttonClasses, carouselConfig.buttonRightClasses]"
                            :tabindex="canScrollNext ? 0 : -1"
                            :dark="dark"
                            @click="scrollNext"
                            @keydown="event => handleKeydown(event, 'next')"
                        />
                        <VIconButton
                            v-if="totalSlides > 1"
                            variant="carousel"
                            :size="buttonSize"
                            icon="chevron-left"
                            data-test="carousel button left"
                            aria-controls="carousel-slides"
                            aria-label="Poprzedni slajd"
                            :class="[carouselConfig.buttonClasses, carouselConfig.buttonLeftClasses]"
                            :tabindex="canScrollPrev ? 0 : -1"
                            :dark="dark"
                            @click="scrollPrev"
                            @keydown="event => handleKeydown(event, 'prev')"
                        />
                    </div>
                </div>
            </div>
        </div>
        <slot
            name="bottom"
            :embla="emblaApi"
        />
    </div>
</template>
